// frontend/src/components/shared/SocialLinks.js
//
// The follow-us icon row: Leadify AI's official Facebook, Instagram and
// LinkedIn pages, in that order (reading order - so Facebook sits on the
// right in Hebrew and on the left in English). Used by the public footer
// (components/homepage/HomeFooter.js) and the contact page (pages/Contact.js).
//
// The URLs come from content/officialProfiles.js, the same single source as
// the homepage's JSON-LD `sameAs` - never type one here.
//
// Icons are lucide-react's outline brand glyphs (already a dependency, the
// same family the contact page draws with): monochrome, one stroke weight,
// bare - no circle or frame (the owner's pick). Each link is a 44px tap
// target with the 19px glyph centred in it; the look lives in
// styles/socialLinks.css.
//
// SEO / prerender: plain anchors with fixed hrefs and labels derived only from
// the `isHebrew` prop, so the prerendered HTML carries them and the first
// render equals the snapshot (no storage, window or Date reads here).

import React from "react";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import { SOCIAL_PROFILES } from "../../content/officialProfiles";
import "../../styles/socialLinks.css";

const ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin };

/** The accessible names. Hebrew: "Leadify AI on <network>" with the ב prefix. */
export function socialLinkLabel(profile, isHebrew) {
  return isHebrew
    ? `לידיפיי ב${profile.name.he} (נפתח בכרטיסייה חדשה)`
    : `Leadify AI on ${profile.name.en} (opens in a new tab)`;
}

export const SOCIAL_LIST_LABEL = {
  he: "לידיפיי ברשתות החברתיות",
  en: "Leadify AI on social media",
};

/**
 * @param {object} props
 * @param {boolean} [props.isHebrew]
 * @param {string} [props.className] extra class for the placement (margins).
 */
export default function SocialLinks({ isHebrew = true, className }) {
  return (
    <ul
      className={className ? `lf-social ${className}` : "lf-social"}
      aria-label={isHebrew ? SOCIAL_LIST_LABEL.he : SOCIAL_LIST_LABEL.en}
    >
      {SOCIAL_PROFILES.map((profile) => {
        const Icon = ICONS[profile.id];
        return (
          <li key={profile.id}>
            <a
              href={profile.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={socialLinkLabel(profile, isHebrew)}
              data-network={profile.id}
            >
              <Icon aria-hidden="true" focusable="false" size={19} strokeWidth={1.6} />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
